import { useState, useMemo } from 'react';
import { FiltersType } from '../../types';

const INITIAL_FILTERS: FiltersType = {
  hasUpdates: false,
  external: {
    all: true,
    orders: false,
    warnings: false,
    advisories: false,
    shelter_in_place: false,
  },
  internal: {
    all: true,
    orders: false,
    warnings: false,
    advisories: false,
    shelter_in_place: false,
  },
};

type UseEvacZoneFiltersReturn = {
  filters: FiltersType;
  isFiltered: boolean;
  handleCheckboxChange: (key: keyof FiltersType, subKey?: string) => void;
};

const getUpdatedFilters = (
  prev: FiltersType,
  key: keyof FiltersType,
  subKey?: string,
): FiltersType => {
  // Handle hasUpdates toggle - reset other filters to default state
  if (key === 'hasUpdates') {
    return {
      hasUpdates: !prev.hasUpdates,
      external: { all: true },
      internal: { all: true },
    };
  }

  // We expect key to be either 'external' or 'internal' at this point
  if (!subKey || subKey === 'all') {
    const resetGroup: Record<string, boolean> = Object.fromEntries(
      Object.keys(prev[key]).map((curr) => [curr, curr === 'all']),
    );

    return { ...prev, [key]: resetGroup };
  }

  // Handle individual filter selection
  return {
    ...prev,
    [key]: {
      ...prev[key],
      all: false,
      [subKey]: !prev[key][subKey],
    },
  };
};

export const useEvacZoneFilters = (): UseEvacZoneFiltersReturn => {
  const [filters, setFilters] = useState<FiltersType>(INITIAL_FILTERS);

  const handleCheckboxChange = (
    key: keyof FiltersType,
    subKey?: string,
  ): void => {
    setFilters((prev) => getUpdatedFilters(prev, key, subKey));
  };

  // check if filters are active
  const isFiltered = useMemo(() => {
    return (
      filters.hasUpdates ||
      Object.entries(filters.external)
        .filter(([key]) => key !== 'all')
        .some(([, value]) => value) ||
      Object.entries(filters.internal)
        .filter(([key]) => key !== 'all')
        .some(([, value]) => value)
    );
  }, [filters]);

  return {
    filters,
    isFiltered,
    handleCheckboxChange,
  };
};
